import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { faArrowLeft, faBoxArchive } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Loader } from "@/components";
import { useMediaJobSocket } from "@/pages/Private/helpers/useMediaJobSocket";
import { MediaJobType } from "@/pages/Private/redux/media/media.schema";
import {
	useActivateStoryboardVersionMutation,
	useCreateWizardStyleMutation,
	useUpdateWizardStyleMutation,
	useAddStoryboardSceneMutation,
	useApproveSceneVideoMutation,
	useApproveWizardStepMutation,
	useArchiveVideoWizardMutation,
	useDeleteStoryboardSceneMutation,
	useExportVideoMutation,
	useGenerateSceneImagesMutation,
	useGenerateSceneVideosMutation,
	useGenerateStoryboardMutation,
	useGetVideoWizardQuery,
	useGenerateNarrationMutation,
	useGenerateNarrationScriptMutation,
	useUpdateSceneScriptMutation,
	usePreviewNarrationMutation,
	useSaveTimelineMutation,
	useStartVideoWizardMutation,
	useUpdateStoryboardSceneMutation,
	useUpdateVideoWizardMutation,
} from "@/pages/Private/redux/media/videoWizard.api";
import {
	MediaErrorCode,
	readMediaError,
	WizardAudioConfig,
	WizardExportConfig,
	WizardTimelineClip,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { api } from "@/redux/api";
import { ROUTE_CONFIG } from "@/routes/config";

import { ArchiveWizardModal } from "./ArchiveWizardModal";
import { CostRail } from "./CostRail";
import { DownstreamApprovedModal } from "./DownstreamApprovedModal";
import { Step1Storyboard } from "./steps/Step1Storyboard";
import { Step2Style } from "./steps/Step2Style";
import { Step3Narration } from "./steps/Step3Narration";
import { Step4Audio } from "./steps/Step4Audio";
import { Step5SceneVideos } from "./steps/Step5SceneVideos";
import { Step6CutExport } from "./steps/Step6CutExport";
import { failureTextKey, hasWorkInFlight } from "./wizard.utils";
import { WizardStepper } from "./WizardStepper";

/** A call the server refused as destructive, held so the modal can retry it with force. */
type PendingForce = (force: boolean) => Promise<unknown>;

/**
 * How often the aggregate is re-read while a job is outstanding. Short enough that a card
 * whose push was missed still settles in seconds, and it only runs while the server owes
 * a result, so an idle wizard costs nothing.
 */
const WORK_IN_FLIGHT_POLL_MS = 5000;

/**
 * The intro-video wizard.
 *
 * A route rather than a modal: it is long-lived, deep-linkable and reload-safe. A reload
 * is simply a GET, because the session lives on the server — this component keeps only
 * which step is on screen and text the author has not sent yet.
 *
 * Nothing here decides whether a step is unlocked, approved or affordable. Every one of
 * those answers arrives in the aggregate, and every mutation returns a fresh one.
 */
export const VideoWizard: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const courseId = Number(id);

	const [sessionId, setSessionId] = useState<number | null>(null);
	const [activeStep, setActiveStep] = useState(1);
	const [pendingForce, setPendingForce] = useState<{ run: PendingForce; steps: number[] } | null>(
		null
	);
	const [errorMessage, setErrorMessage] = useState("");

	const [startWizard, { isLoading: isStarting }] = useStartVideoWizardMutation();
	const [updateWizard, { isLoading: isPatching }] = useUpdateVideoWizardMutation();
	const [createStyle, { isLoading: isCreatingStyle }] = useCreateWizardStyleMutation();
	const [updateStyle, { isLoading: isUpdatingStyle }] = useUpdateWizardStyleMutation();
	const [generateStoryboard, { isLoading: isGenerating }] = useGenerateStoryboardMutation();
	const [activateVersion, { isLoading: isActivating }] = useActivateStoryboardVersionMutation();
	const [addScene, { isLoading: isAdding }] = useAddStoryboardSceneMutation();
	const [updateScene, { isLoading: isUpdatingScene }] = useUpdateStoryboardSceneMutation();
	const [deleteScene, { isLoading: isDeleting }] = useDeleteStoryboardSceneMutation();
	const [generateSceneImages, { isLoading: isGeneratingImages }] = useGenerateSceneImagesMutation();
	const [generateSceneVideos, { isLoading: isGeneratingVideos }] = useGenerateSceneVideosMutation();
	const [approveSceneVideo, { isLoading: isApprovingScene }] = useApproveSceneVideoMutation();
	const [previewNarration, { isLoading: isPreviewing }] = usePreviewNarrationMutation();
	const [generateNarrationScript, { isLoading: isGeneratingScript }] =
		useGenerateNarrationScriptMutation();
	const [updateSceneScript, { isLoading: isEditingScript }] = useUpdateSceneScriptMutation();
	const [generateNarration, { isLoading: isGeneratingNarration }] = useGenerateNarrationMutation();
	const [saveTimeline, { isLoading: isSavingTimeline }] = useSaveTimelineMutation();
	const [exportVideo, { isLoading: isExporting }] = useExportVideoMutation();
	const [approveStep, { isLoading: isApproving }] = useApproveWizardStepMutation();
	const [archiveWizard, { isLoading: isArchiving }] = useArchiveVideoWizardMutation();
	const [isArchiveOpen, setArchiveOpen] = useState(false);

	const {
		data: wizard,
		isLoading,
		refetch,
	} = useGetVideoWizardQuery(sessionId as number, {
		skip: sessionId === null,
		// Sockets are an accelerator, not a guarantee; a focus refetch is the client-side
		// backstop, and the reconciliation cron is the server-side one.
		refetchOnFocus: true,
	});

	// POST is create-or-resume, so opening the route is the same call whether the author
	// has been here before or not.
	useEffect(() => {
		if (!courseId || sessionId !== null) {
			return;
		}

		startWizard(courseId)
			.unwrap()
			.then((session) => setSessionId(session.id))
			.catch(() => setErrorMessage(ts("errors.couldNotStart")));
	}, [courseId, sessionId, startWizard, ts]);

	// How far the wizard had unlocked last time we looked. The ref is what makes the
	// effect below edge-triggered: it advances when a step is *newly* unlocked, and stays
	// out of the way when the author navigates back to an earlier step on their own.
	const unlockedSoFar = useRef(1);

	useEffect(() => {
		if (!wizard) {
			return;
		}

		// Approving a step is what unlocks the next one, so that is the moment to move the
		// author on — otherwise the button appears to do nothing and they have to find the
		// stepper themselves.
		if (wizard.unlockedStep > unlockedSoFar.current) {
			setActiveStep(wizard.unlockedStep);
		}

		unlockedSoFar.current = wizard.unlockedStep;
		setActiveStep((step) => Math.min(Math.max(step, 1), wizard.unlockedStep));
	}, [wizard]);

	/**
	 * The poll that makes the socket optional rather than load-bearing. A dropped
	 * `job.succeeded` used to strand a card in its spinner until some unrelated mutation
	 * happened to return a fresh aggregate — the artifact was long since READY on the
	 * server, and nothing on the client ever went back to look.
	 */
	const workInFlight = hasWorkInFlight(wizard);

	useEffect(() => {
		if (!workInFlight) {
			return;
		}

		const timer = setInterval(() => refetch(), WORK_IN_FLIGHT_POLL_MS);

		return () => clearInterval(timer);
	}, [workInFlight, refetch]);

	useMediaJobSocket({
		courseId,
		wizardSessionId: sessionId ?? undefined,
		// The socket carries the whole aggregate, but refetching keeps one code path for
		// "the truth changed" whether the push arrived or not.
		onWizardUpdated: () => refetch(),
		onJobUpdated: () => refetch(),
		onJobSucceeded: (event) => {
			refetch();

			// A finished export may have replaced the scenario's intro video, and the course
			// page's cached data still shows the old one — invalidating is what makes the
			// scenario play the new trailer without a hard refresh.
			if (event.type === MediaJobType.VIDEO_EXPORT) {
				dispatch(api.util.invalidateTags(["Courses"]));
			}
		},
		/**
		 * A failed job is the one thing the aggregate cannot say on its own: it leaves
		 * `activeJobs` the moment it fails, so refetching alone would take the spinner away
		 * and explain nothing. A safety refusal is the author's to act on, and telling them
		 * only that something went wrong invites them to retry the identical request — so
		 * a refusal is worded from its code in the UI's language, and anything else shows
		 * the reason the event carries.
		 */
		onJobFailed: (event) => {
			refetch();

			const key = failureTextKey(event.errorCode);

			setErrorMessage(key ? ts(key) : event.errorMessage || ts("errors.jobFailed"));
		},
	});

	const isBusy =
		isStarting ||
		isPatching ||
		isGenerating ||
		isActivating ||
		isAdding ||
		isUpdatingScene ||
		isDeleting ||
		isGeneratingImages ||
		isGeneratingVideos ||
		isApprovingScene ||
		isCreatingStyle ||
		isUpdatingStyle ||
		isPreviewing ||
		isGeneratingScript ||
		isEditingScript ||
		isGeneratingNarration ||
		isSavingTimeline ||
		isExporting ||
		isApproving;

	/**
	 * Runs a mutation the server may refuse as destructive. On 409 DOWNSTREAM_APPROVED the
	 * call is held and the confirm dialog opened; confirming replays exactly the same call
	 * with `force: true`. Everything else surfaces as an error.
	 */
	const runForceable = useCallback(
		async (run: PendingForce) => {
			setErrorMessage("");

			try {
				await run(false);
			} catch (error) {
				const body = readMediaError(error);

				if (body?.code === MediaErrorCode.DOWNSTREAM_APPROVED) {
					setPendingForce({ run, steps: body.steps ?? [] });

					return;
				}

				if (body?.code === MediaErrorCode.STALE_VERSION) {
					// Someone else moved the wizard on. Re-reading is the whole fix.
					await refetch();
					setErrorMessage(ts("errors.staleVersion"));

					return;
				}

				// Archived elsewhere (BCM360-375): the server refuses every write to it.
				if (body?.code === MediaErrorCode.SESSION_ARCHIVED) {
					setErrorMessage(ts("errors.sessionArchived"));

					return;
				}

				setErrorMessage(body?.message || ts("errors.generic"));
			}
		},
		[refetch, ts]
	);

	const confirmForce = useCallback(async () => {
		if (!pendingForce) {
			return;
		}

		try {
			await pendingForce.run(true);
			setPendingForce(null);
		} catch (error) {
			setPendingForce(null);
			setErrorMessage(readMediaError(error)?.message || ts("errors.generic"));
		}
	}, [pendingForce, ts]);

	const handlers = useMemo(() => {
		if (!wizard) {
			return null;
		}

		const wizardId = wizard.id;

		return {
			/**
			 * Forceable because one of these settings is not like the others: changing the
			 * language rewrites the storyboard, so the server refuses it with a 409 once
			 * there is approved work below and the modal asks before discarding it.
			 */
			patch: (patch: {
				prompt?: string;
				targetLengthSeconds?: number;
				styleId?: string;
				narrationLanguage?: string;
			}) =>
				runForceable((force) =>
					updateWizard({ id: wizardId, version: wizard.version, ...patch, force }).unwrap()
				),
			/**
			 * A picture already in the library becomes a style. Forceable like the generate
			 * calls: a new style is only worth having if it can be generated with, and doing
			 * so resets what the steps behind it were built on.
			 */
			createStyle: (courseFileId: number) =>
				runForceable((force) => createStyle({ id: wizardId, courseFileId, force }).unwrap()),
			/** The author's own wording. The server stales that style's images in reply. */
			updateStyle: (styleRowId: number, promptFragment: string) =>
				runForceable((force) =>
					updateStyle({ id: wizardId, styleRowId, promptFragment, force }).unwrap()
				),
			selectModel: (aiModelId: number) =>
				runForceable(() =>
					updateWizard({
						id: wizardId,
						version: wizard.version,
						stepModels: { [String(activeStep)]: aiModelId },
					}).unwrap()
				),
			generate: (feedback?: string) =>
				runForceable((force) => generateStoryboard({ id: wizardId, feedback, force }).unwrap()),
			selectVersion: (versionId: number) =>
				runForceable((force) => activateVersion({ id: wizardId, versionId, force }).unwrap()),
			updateScene: (
				sceneId: number,
				patch: { title?: string; description?: string; durationSeconds?: number }
			) => runForceable(() => updateScene({ id: wizardId, sceneId, ...patch }).unwrap()),
			addScene: () => runForceable((force) => addScene({ id: wizardId, force }).unwrap()),
			deleteScene: (sceneId: number) =>
				runForceable((force) => deleteScene({ id: wizardId, sceneId, force }).unwrap()),
			generateSceneImages: (sceneIds?: number[], feedback?: string) =>
				runForceable((force) =>
					generateSceneImages({ id: wizardId, sceneIds, feedback, force }).unwrap()
				),
			// Forceable like a generate call: once step 4 is approved, changing the voice,
			// the music or the subtitles resets what the spoken take was built on.
			patchAudio: (audioConfig: WizardAudioConfig) =>
				runForceable((force) =>
					updateWizard({ id: wizardId, version: wizard.version, audioConfig, force }).unwrap()
				),
			generateSceneVideos: (sceneIds?: number[], feedback?: string) =>
				runForceable((force) =>
					generateSceneVideos({ id: wizardId, sceneIds, feedback, force }).unwrap()
				),
			// Approving one scene only ever moves forward, so there is nothing to reset and
			// nothing to confirm — it is the one wizard action that cannot raise a 409.
			approveSceneVideo: (sceneId: number) =>
				runForceable(() => approveSceneVideo({ id: wizardId, sceneId }).unwrap()),
			previewNarration: () => runForceable(() => previewNarration({ id: wizardId }).unwrap()),
			// Forceable because new words replace what the take and the cut were built on.
			// Naming scenes rewrites only those; the untouched ones keep their words.
			generateNarrationScript: (feedback?: string, brief?: string, sceneIds?: number[]) =>
				runForceable((force) =>
					generateNarrationScript({ id: wizardId, feedback, brief, sceneIds, force }).unwrap()
				),
			// The author's own words for one scene. Forceable for the same reason: the
			// spoken take no longer says them.
			updateSceneScript: (sceneId: number, text: string) =>
				runForceable((force) => updateSceneScript({ id: wizardId, sceneId, text, force }).unwrap()),
			// Forceable because a second take replaces what the later steps were built on.
			generateNarration: () =>
				runForceable((force) => generateNarration({ id: wizardId, force }).unwrap()),
			// Forceable like the generate calls: re-cutting after a finished export replaces
			// the video the scenario is already using.
			saveTimeline: (clips: WizardTimelineClip[]) =>
				runForceable((force) =>
					saveTimeline({ id: wizardId, version: wizard.version, clips, force }).unwrap()
				),
			patchExport: (exportConfig: WizardExportConfig) =>
				runForceable((force) =>
					updateWizard({ id: wizardId, version: wizard.version, exportConfig, force }).unwrap()
				),
			exportVideo: () => runForceable((force) => exportVideo({ id: wizardId, force }).unwrap()),
			approve: (step: number) =>
				runForceable((force) => approveStep({ id: wizardId, step, force }).unwrap()),
		};
	}, [
		wizard,
		activeStep,
		runForceable,
		updateWizard,
		generateStoryboard,
		createStyle,
		updateStyle,
		activateVersion,
		updateScene,
		addScene,
		deleteScene,
		generateSceneImages,
		generateSceneVideos,
		approveSceneVideo,
		previewNarration,
		generateNarrationScript,
		updateSceneScript,
		generateNarration,
		saveTimeline,
		exportVideo,
		approveStep,
	]);

	/**
	 * Archiving ends the session for good (BCM360-375): the author lands back in the
	 * scenario editor, whose tab now offers a fresh start and lists this one as archived.
	 */
	const confirmArchive = async () => {
		if (!wizard) {
			return;
		}

		setErrorMessage("");

		try {
			await archiveWizard({ id: wizard.id, version: wizard.version }).unwrap();
			setArchiveOpen(false);
			navigate(`${ROUTE_CONFIG.COURSES}/edit/${courseId}`);
		} catch (error) {
			const body = readMediaError(error);

			setArchiveOpen(false);

			if (body?.code === MediaErrorCode.STALE_VERSION) {
				await refetch();
			}

			setErrorMessage(
				body?.code === MediaErrorCode.WORK_IN_FLIGHT
					? ts("archive.workInFlight")
					: body?.code === MediaErrorCode.STALE_VERSION
						? ts("errors.staleVersion")
						: body?.code === MediaErrorCode.SESSION_ARCHIVED
							? ts("errors.sessionArchived")
							: body?.message || ts("errors.generic")
			);
		}
	};

	if (isLoading || !wizard || !handlers) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				{errorMessage ? (
					<p className="text-sm font-semibold text-danger">{errorMessage}</p>
				) : (
					<Loader />
				)}
			</div>
		);
	}

	return (
		<div className="flex min-h-full flex-col">
			<header className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
				<div>
					<h1 className="text-xl font-bold text-ink">{ts("title")}</h1>
					<p className="mt-0.5 text-xs text-subtle">{ts("subtitle")}</p>
				</div>
				{/* `Button` fills its parent, so the two share a row under the title, as the
				    back button alone did before. Archiving is not offered while the server
				    still owes a result: it cancels nothing, so it waits until every job has
				    settled (decided 2026-09-30), and the reason is stated above the row. */}
				<div className="w-full space-y-1.5">
					{hasWorkInFlight(wizard) && (
						<p className="text-xs text-subtle">{ts("archive.workInFlight")}</p>
					)}
					<div className="flex gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isBusy || hasWorkInFlight(wizard)}
							image={<Icon className="mr-2" icon={faBoxArchive} />}
							size={ButtonSize.S}
							title={ts("archive.button")}
							type="button"
							onClick={() => setArchiveOpen(true)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faArrowLeft} />}
							size={ButtonSize.S}
							title={ts("backToScenario")}
							type="button"
							onClick={() => navigate(`${ROUTE_CONFIG.COURSES}/edit/${courseId}`)}
						/>
					</div>
				</div>
			</header>

			<WizardStepper currentStep={activeStep} wizard={wizard} onSelect={setActiveStep} />

			<div className="flex flex-col gap-5 p-5 lg:flex-row">
				<main className="min-w-0 flex-1">
					{!!errorMessage && (
						<p className="mb-3 rounded-card bg-error-light px-4 py-2.5 text-sm font-semibold text-text">
							{errorMessage}
						</p>
					)}

					{activeStep === 1 ? (
						<Step1Storyboard
							isBusy={isBusy}
							wizard={wizard}
							onAddScene={handlers.addScene}
							onApprove={() => handlers.approve(1)}
							onDeleteScene={handlers.deleteScene}
							onGenerate={handlers.generate}
							onPatch={handlers.patch}
							onSelectModel={handlers.selectModel}
							onSelectVersion={handlers.selectVersion}
							onUpdateScene={handlers.updateScene}
						/>
					) : activeStep === 2 ? (
						<Step2Style
							courseId={courseId}
							isBusy={isBusy}
							wizard={wizard}
							onApprove={() => handlers.approve(2)}
							onCreateStyle={handlers.createStyle}
							onGenerate={handlers.generateSceneImages}
							onSelectModel={handlers.selectModel}
							onSelectStyle={(styleId) => handlers.patch({ styleId })}
							onUpdateStyle={handlers.updateStyle}
						/>
					) : activeStep === 3 ? (
						<Step3Narration
							isBusy={isBusy}
							wizard={wizard}
							onApprove={() => handlers.approve(3)}
							onEditSceneText={handlers.updateSceneScript}
							onGenerate={handlers.generateNarrationScript}
							onSelectModel={handlers.selectModel}
						/>
					) : activeStep === 4 ? (
						<Step4Audio
							isBusy={isBusy}
							wizard={wizard}
							onApprove={() => handlers.approve(4)}
							onGenerate={handlers.generateNarration}
							onPatchAudio={handlers.patchAudio}
							onPreview={handlers.previewNarration}
							onSelectModel={handlers.selectModel}
						/>
					) : activeStep === 5 ? (
						<Step5SceneVideos
							isBusy={isBusy}
							wizard={wizard}
							onApprove={() => handlers.approve(5)}
							onApproveScene={handlers.approveSceneVideo}
							onGenerate={handlers.generateSceneVideos}
							onSelectModel={handlers.selectModel}
						/>
					) : (
						<Step6CutExport
							isBusy={isBusy}
							wizard={wizard}
							onExport={handlers.exportVideo}
							onPatchExport={handlers.patchExport}
							onSaveTimeline={handlers.saveTimeline}
						/>
					)}
				</main>

				<CostRail wizard={wizard} />
			</div>

			<ArchiveWizardModal
				isLoading={isArchiving}
				isOpened={isArchiveOpen}
				onCancel={() => setArchiveOpen(false)}
				onConfirm={confirmArchive}
			/>

			<DownstreamApprovedModal
				isLoading={isBusy}
				isOpened={!!pendingForce}
				steps={pendingForce?.steps ?? []}
				onCancel={() => setPendingForce(null)}
				onConfirm={confirmForce}
			/>
		</div>
	);
};
